"use client"

import { css } from "@emotion/css"
import { skipToken, useQuery } from "@tanstack/react-query"
import { useAtomValue } from "jotai"
import { useContext } from "react"
import { useTranslation } from "react-i18next"

import { getCourseMaterialChapterExerciseProgress } from "@/generated/course-material-api/sdk.generated"
import type { PageWithExercises } from "@/generated/course-material-api/types.generated"
import ExerciseBox from "@/shared-module/common/components/ExerciseList/ExerciseBox"
import PageBox from "@/shared-module/common/components/ExerciseList/PageBox"
import LoginStateContext from "@/shared-module/common/contexts/LoginStateContext"
import { baseTheme, primaryFont } from "@/shared-module/common/styles"
import { QueryResult } from "@/shared-module/components"
import { courseMaterialAtom } from "@/state/course-material"
import { coursePageSectionRoute } from "@/utils/course-material/routing"

export interface ChapterExerciseListGroupedByPageProps {
  chapterId: string
  courseInstanceId: string | undefined
  courseSlug: string
  organizationSlug: string
  page: PageWithExercises
}

const CHAPTER_EXERCISE_PROGRESS_QUERY_KEY = "courseMaterialChapterExerciseProgress"

const ChapterExerciseListGroupedByPage: React.FC<
  React.PropsWithChildren<ChapterExerciseListGroupedByPageProps>
> = ({ chapterId, courseInstanceId, courseSlug, organizationSlug, page }) => {
  const loginStateContext = useContext(LoginStateContext)
  const { t } = useTranslation()
  const courseMaterialState = useAtomValue(courseMaterialAtom)

  const course = courseMaterialState.course
  const chapterLockingEnabled = course?.chapter_locking_enabled ?? false
  const getUserCourseInstanceChapterExercisesProgress = useQuery({
    queryKey: [CHAPTER_EXERCISE_PROGRESS_QUERY_KEY, courseInstanceId, chapterId] as const,
    queryFn: courseInstanceId
      ? () =>
          getCourseMaterialChapterExerciseProgress({
            path: {
              course_instance_id: courseInstanceId,
              chapter_id: chapterId,
            },
          })
      : skipToken,
    select: (data) => {
      return new Map(data.map((x) => [x.exercise_id, x.score_given]))
    },
    enabled: Boolean(courseInstanceId),
  })

  const renderContent = (progress: Map<string, number> | undefined) => {
    if (page.exercises.length === 0) {
      return null
    }
    return (
      <>
        <PageBox pageTitle={page.title} />
        {chapterLockingEnabled && (
          <div
            className={css`
              padding: 1rem;
              background-color: ${baseTheme.colors.yellow[50]};
              border-left: 4px solid ${baseTheme.colors.yellow[500]};
              border-radius: 4px;
              margin: 1rem 0;
            `}
          >
            <p
              className={css`
                margin: 0;
                font-family: ${primaryFont};
                font-size: 0.9375rem;
                line-height: 1.6;
                color: ${baseTheme.colors.gray[700]};
              `}
            >
              {t("exercises-done-through-locking-notification")}
            </p>
          </div>
        )}
        <div>
          {page.exercises.map((e) => {
            let userPoints = null

            if (loginStateContext.signedIn) {
              userPoints = progress?.get(e.id) ?? 0
            }
            return (
              <div key={e.id}>
                <ExerciseBox
                  url={coursePageSectionRoute(organizationSlug, courseSlug, page.url_path, e.id)}
                  bg={"rgb(242, 245, 247)"}
                  exerciseIndex={e.order_number + 1}
                  exerciseTitle={e.name}
                  scoreMaximum={e.score_maximum}
                  userPoints={userPoints}
                />
              </div>
            )
          })}
        </div>
      </>
    )
  }

  // When the query is disabled (e.g. logged out or no course instance) it never fetches and has no
  // data, so QueryResult would render nothing. Render the exercises directly (without progress) instead.
  if (!courseInstanceId) {
    return renderContent(undefined)
  }

  return (
    <QueryResult query={getUserCourseInstanceChapterExercisesProgress} minHeight={45}>
      {(progress) => renderContent(progress)}
    </QueryResult>
  )
}

export default ChapterExerciseListGroupedByPage
